// The WGSL twins of the shared pieces of `../territory.ts`: the rock mask
// and the dash arithmetic that the outline twins of `geo.ts` call (R495).
//
// **A twin writes BODIES.** The uniform block, the bindings and the varyings
// are generated from the pass's `UniformSpec[]` (`./prelude.ts`
// `worldHeaderWgsl`), so no twin here declares a uniform and every read is
// `U.uName`.

/**
 * The rock mask's terrain-plane branch, as WGSL.
 *
 * It is the twin of `../territory.ts` `ROCK_PLANE`, function for function.
 * The four uniforms it reads stay `ROCK_PLANE_UNIFORMS`: a WGSL twin declares
 * no uniform of its own, because the whole block is generated from the spec
 * list in front of the body.
 *
 * The GLSL reads the plane with `texture()`. The twin reads it with
 * `textureSampleLevel(…, 0.0)` and never with `textureSample`, because the
 * five taps sit under an `if` and a sampling call in non-uniform control flow
 * fails the uniformity analysis. The plane carries one mip level, so an
 * explicit level 0 is the same tap.
 */
export const ROCK_PLANE_WGSL = `
// The world point p as a uv of the TERRAIN plane (../../terrain-prep.ts). It
// is the terrain pass's own helper, written out again here because the two
// passes share no source, only the plane and the three figures that place it.
fn distUv(p: vec2f) -> vec2f { return (p - U.uDistLo) * U.uDistUv; }

// How much of a pixel the ROCK leaves, read off the TERRAIN plane.
//
// It reads fR from the plane's A channel and takes the central difference
// the terrain composite takes, so at terrain=tex the hole edge is the same
// curve as the ridge body edge. U.uRockPlane 0 answers 1.0 and leaves the
// caller its own mask: the plane lands a moment after the hello, and the
// rock knob selects the kernel for a measurement.
fn rockPlaneWater(p: vec2f, px: f32) -> f32 {
  if (U.uRockPlane != 1) { return 1.0; }
  let uv = distUv(p);
  let texel = U.uDistUv * U.uDistStep;
  let tx = vec2f(texel.x, 0.0);
  let ty = vec2f(0.0, texel.y);
  let fRp = textureSampleLevel(uDist, sLinear, uv, 0.0).a;
  let xp = textureSampleLevel(uDist, sLinear, uv + tx, 0.0).a;
  let xm = textureSampleLevel(uDist, sLinear, uv - tx, 0.0).a;
  let yp = textureSampleLevel(uDist, sLinear, uv + ty, 0.0).a;
  let ym = textureSampleLevel(uDist, sLinear, uv - ty, 0.0).a;
  let dfRp = vec2f((xp - xm) / (2.0 * U.uDistStep.x),
                   (yp - ym) / (2.0 * U.uDistStep.y));
  let sRp = (fRp - 0.5) / max(length(dfRp), 1e-5);
  return 1.0 - fillCov(sRp, px);
}
`;

/**
 * The dash arithmetic along a line, as WGSL (R1568, R1577).
 *
 * It is the twin of `../territory.ts` `DASH_ALONG`, function for function.
 * The three uniforms it reads stay `DASH_UNIFORMS`, and `U.uWidthScale`
 * beside them.
 */
export const DASH_ALONG_WGSL = `
fn dashRun(u: f32, m: f32, h: f32, period: f32) -> f32 {
  var a = u - m;
  a -= period * floor(a / period + 0.5);
  return select(max(abs(a) - h, 0.0), 1e9, h < 0.0);
}

fn dashAlong(f: i32, s: f32) -> f32 {
  let b = U.uDashB[f];
  let period = b.z;
  if (U.uEdgeDash != 1 || period <= 0.0) { return 0.0; }
  let a = U.uDashA[f];
  let u = s - period * floor(s / period);
  let e = min(dashRun(u, a.x, a.y, period),
              min(dashRun(u, a.z, a.w, period), dashRun(u, b.x, b.y, period)));
  return e * U.uWidthScale;
}
`;
